import { getFsContextUserIds } from '@fcsmc/react-loading-components'
import { skipToken, useSuspenseQuery } from '@apollo/client'
import { CUSTOMER_DATA_QUERY } from '@gql/Queries'
import { Country, Fc__Account__PinnacleAccount, Fmcc__Payment__EligibilityDetails } from '@src/__generated__/graphql'
import { createContext, Dispatch, ReactNode, useContext, useEffect, useState } from 'react'

interface AccountContextProps {
  selectedAccount: Fc__Account__PinnacleAccount
  paymentEligibilityDetails: Fmcc__Payment__EligibilityDetails | undefined | null
  allAccounts: Array<Fc__Account__PinnacleAccount>
  setSelectedAccount: Dispatch<Fc__Account__PinnacleAccount>
}

export const AccountContext = createContext<AccountContextProps | undefined>(undefined)

export const AccountProvider = ({ children }: { children: ReactNode }) => {
  const [selectedAccount, setSelectedAccount] = useState<Fc__Account__PinnacleAccount>()
  const { mcn } = getFsContextUserIds()

  if (mcn === undefined || mcn === null) {
    throw new Error('No MCN found')
  }

  const { data } = useSuspenseQuery(
    CUSTOMER_DATA_QUERY,
    mcn
      ? {
          variables: {
            mcn: mcn.toString(),
            masterCustomerNumber: mcn,
            country: Country.Us,
          },
        }
      : skipToken
  )

  if (
    data?.fmcc?.account?.accountsByMCN === undefined ||
    data?.fmcc?.account?.accountsByMCN === null ||
    data?.fmcc?.account?.accountsByMCN.length === 0
  ) {
    throw new Error('No accounts found')
  }

  const allAccounts: Array<Fc__Account__PinnacleAccount> = data?.fmcc.account.accountsByMCN

  useEffect(() => {
    if (selectedAccount === undefined) {
      setSelectedAccount(data?.fmcc?.account?.accountsByMCN?.[0] ?? undefined)
    } else {
      setSelectedAccount(
        data?.fmcc?.account?.accountsByMCN?.find((account) => account.accountNumber === selectedAccount.accountNumber)
      )
    }
  }, [data])

  if (selectedAccount === undefined) {
    return null
  }

  const multiPaymentAccountInformation = data?.fmcc?.payment?.multiPaymentInformation?.multiPaymentAccountInformation
  const paymentEligibilityDetails: Fmcc__Payment__EligibilityDetails | undefined | null =
    multiPaymentAccountInformation?.find(
      (paymentInfo) => paymentInfo?.account?.id.toString() === selectedAccount?.accountNumber?.toString()
    )?.paymentEligibilityDetails

  return (
    <AccountContext.Provider value={{ selectedAccount, paymentEligibilityDetails, allAccounts, setSelectedAccount }}>
      {children}
    </AccountContext.Provider>
  )
}

export const useAccount = () => {
  const context = useContext(AccountContext)
  if (context === undefined) {
    throw new Error('useAccount must be used within an AccountProvider')
  }
  return context
}
